import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote, X } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

// Retention nudge: once a creator's withdrawable balance (sales + tips) crosses the
// payout minimum, remind them there's real money waiting. Dismiss is week-scoped so
// it re-appears next week if they still haven't cashed out.
export default function PayoutReadyBanner() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    api.get("/marketplace/me/wallet").then((r) => setWallet(r.data || null)).catch(() => setWallet(null));
  }, []);

  const weekStamp = Math.floor(Date.now() / WEEK_MS);
  const dismissed = typeof window !== "undefined" && localStorage.getItem("gv_payout_ready_dismissed") === String(weekStamp);
  const min = Number(wallet?.min_payout || 0);
  const available = Number(wallet?.available || 0);
  if (!wallet || hidden || dismissed || min <= 0 || available < min) return null;

  const dismiss = () => { localStorage.setItem("gv_payout_ready_dismissed", String(weekStamp)); setHidden(true); };
  const tips = Number(wallet?.tips || 0);

  return (
    <div className="relative rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 to-transparent p-5 mb-6" data-testid="payout-ready-banner">
      <button onClick={dismiss} className="absolute top-3 right-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="payout-ready-dismiss" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-emerald-500/20 shrink-0">
          <Banknote className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-bold text-emerald-300">
            You've got <span className="tabular-nums" data-testid="payout-ready-amount">{usd(available)}</span> ready to withdraw 💚
          </div>
          <div className="text-sm text-[hsl(var(--foreground))] mt-0.5">
            You've passed the {usd(min)} payout minimum — cash out your sales{tips > 0 ? " + tips" : ""}, or convert it to AI credits.
          </div>
          <Button onClick={() => navigate("/app/earnings")} size="sm"
            className="rounded-full mt-3 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="payout-ready-cta">
            <Banknote className="w-3.5 h-3.5" /> Go to Earnings
          </Button>
        </div>
      </div>
    </div>
  );
}
